import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./_utils";

const cardVariants = cva("rounded-lg", {
  variants: {
    variant: {
      // Print — the committed record. Opaque, flat, on the page.
      default:
        "bg-[var(--print-bg)] border border-[var(--print-border)] shadow-[var(--shadow-print)]",
      muted: "bg-bg-surface",
      outlined: "bg-transparent border border-border",
      // Glass — staged, waiting on a human.
      staged: "material-glass rounded-2xl",
      // Dashed — not yet material.
      dashed: "material-dashed",
    },
    padding: {
      none: "p-0",
      sm: "p-sm",
      md: "p-md",
      lg: "p-lg",
    },
    tone: {
      neutral: "",
      warning: "bg-warning-muted border border-warning/30 text-text-primary",
      success: "bg-success-muted border border-success/30 text-text-primary",
      error: "bg-error-muted border border-error/30 text-text-primary",
      info: "bg-info-muted border border-info/30 text-text-primary",
    },
  },
  defaultVariants: { variant: "default", padding: "md", tone: "neutral" },
});

type CardProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardVariants>;

/**
 * Grouping container. `variant` controls fill style (default/muted/outlined);
 * `tone` layers a semantic color (warning/success/error/info) on top — tone
 * classes override the variant's background and border when non-neutral.
 *
 * @example
 * <Card>Content</Card>
 * <Card padding="none">No padding</Card>
 * <Card variant="muted" padding="lg">Muted, roomy</Card>
 * <Card variant="outlined" padding="sm">Thin outlined</Card>
 * <Card tone="warning" padding="sm">Attention needed</Card>
 * <Card tone="success">Done</Card>
 */
export function Card({
  className,
  variant,
  padding,
  tone,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(cardVariants({ variant, padding, tone }), className)}
      {...props}
    />
  );
}
